import { Link } from "react-router-dom";
import { LinkedinLogo } from "@phosphor-icons/react";
import { C } from "@/brand/tokens";
import { brand6, footerColumns6 } from "@/brand/contentF6";
import { LogoWordmark } from "@/components/brand/LogoWordmark";

function FooterLink({ label, href }) {
  const style = { color: C.whiteOnDark };
  const hover = {
    onMouseEnter: (e) => (e.currentTarget.style.color = C.accentMetal),
    onMouseLeave: (e) => (e.currentTarget.style.color = C.whiteOnDark),
  };
  if (!href) {
    return (
      <span className="block font-inter text-[14px]" style={style}>
        {label}
      </span>
    );
  }
  if (href.startsWith("/")) {
    return (
      <Link to={href} className="block font-inter text-[14px] transition-colors" style={style} {...hover}>
        {label}
      </Link>
    );
  }
  const external = href.startsWith("http");
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noreferrer" : undefined}
      className="font-inter text-[14px] transition-colors inline-flex items-center gap-2 break-all"
      style={style}
      {...hover}
    >
      {label}
      {external && <LinkedinLogo size={13} weight="regular" style={{ color: C.accentMetal }} />}
    </a>
  );
}

/** SiteFooter6 — Smoked Ink institutional footer. Stacked wordmark, three link columns. */
export function SiteFooter6() {
  return (
    <footer
      data-testid="site-footer-6"
      style={{ background: C.bgDark, color: C.textOnDark, borderTop: `1px solid ${C.brassSoft}` }}
    >
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8 lg:px-14 py-20">
        <div className="grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <LogoWordmark tone="light" height={92} />
            <p className="font-cormorant italic mt-9 max-w-xs" style={{ color: C.accentMetal, fontSize: "1.15rem" }}>
              {brand6.tagline}
            </p>
            <p className="font-inter mt-6 max-w-xs leading-[1.7]" style={{ color: C.whiteOnDark, fontSize: "0.95rem" }}>
              A Leadership, People & Culture advisory firm for mission-driven,
              values-led, public-sector-adjacent, and mid-sized organizations.
            </p>
            <a
              href={brand6.linkedin}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-linkedin"
              className="mt-7 inline-flex items-center gap-2.5 font-inter text-[11px] uppercase font-semibold transition-colors"
              style={{ color: C.accentMetal, letterSpacing: "0.22em" }}
            >
              <LinkedinLogo size={16} weight="regular" />
              Connect on LinkedIn
            </a>
          </div>

          <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-10">
            {footerColumns6.map(([title, items]) => (
              <div key={title}>
                <div
                  className="font-inter text-[10.5px] uppercase font-semibold mb-6 pb-3"
                  style={{ color: C.accentMetal, letterSpacing: "0.26em", borderBottom: `1px solid ${C.brassSoft}` }}
                >
                  {title}
                </div>
                <ul className="space-y-3">
                  {items.map(([label, href]) => (
                    <li key={label}>
                      <FooterLink label={label} href={href} />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div
          className="mt-16 pt-6 flex flex-wrap items-center justify-between gap-3 font-inter text-[10.5px] uppercase font-medium"
          style={{ borderTop: `1px solid ${C.brassSoft}`, color: C.whiteOnDarkSoft, letterSpacing: "0.26em" }}
        >
          <span>© {new Date().getFullYear()} &nbsp;{brand6.legalName}</span>
          <span style={{ color: C.accentMetal }}>{brand6.category}</span>
        </div>
      </div>
    </footer>
  );
}
